<template>
  <div class="wrapper flex column settings-css manage">
    <div
      v-if="!checkPermission('manage_settings') || forbidden" >
      <h1 class="settings__title" >
        <div
          class="back-btn"
          @click="goBack('WAManage')">
          <SBIcon
            class="mr-2"
            :icon="SBIconList.DOWN_ARROW"
            :color="SBColors.greyPrimary"
            :size="12"
            :rotate="90" />
          <p>Profile</p>
        </div>
      </h1>
      <Forbidden />
    </div>
    <div
      v-else-if="!forbidden"
      class="settings--limit-width greedy">

      <div class="flex align-flex-end space-between">
        <h1 class="settings__title" >
          <div
            class="back-btn"
            @click="goBack('WAManage')">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.DOWN_ARROW"
              :color="SBColors.greyPrimary"
              :size="12"
              :rotate="90" />
            <p>Profile</p>
          </div>
        </h1>
        <b-button
          v-if="business && business.phone"
          vaiant="primary"
          class="profile-btn-wrapper flex center-all no-shrink ml-2"
          :href="`http://wa.me/${business.phone.replace('+', '')}`"
          target="_blank" >
          View profile
          <SBIcon
            class="ml-1"
            :icon="SBIconList.ARROW_45"
            :color="SBColors.blackUltralight"
            :size="12" />
        </b-button>
      </div>

      <Spinner
        v-if="loading"
        style="height: 38px;"
        class="my-4 full-width"
        :size="20"
        :thickness="2" />

      <template v-else>
        <label
          for="file"
          class="m-0">
          <div id="logo-wrapper">
            <span
              id="logo"
              :style="logoUrl ? `background-image: url(${logoUrl}); background-size: cover; border: 1px solid ${SBColors.greyBorder};` : ''" />
            <DropHere
              :force="true"
              type="basic" />
          </div>
        </label>

        <ValidationObserver
          ref="customFieldObserver"
          v-slot="{ valid, dirty }" >

          <form @submit.prevent="updateInfo">
            <button
              class="hide"
              type="submit" />

            <b-form-group>
              <label
                for="phone"
                class="flex flex-align-center" >
                Phone number
              </label>
              <ValidationProvider
                id="stop-input"
                v-slot="{ errors }"
                name="phone"
                rules="required">
                <b-input
                  :value="$options.filters.phoneFormat(business.phone)"
                  disabled
                  autocomplete="off"
                  class="sb2-bordered sb2-hoverable sb2-focusable" />
                <div
                  v-if="errors[0]"
                  class="error flex flex-align-center">
                  <SBIcon
                    class="mr-2"
                    :icon="SBIconList.ERROR"
                    :color="SBColors.redPrimary"
                    :size="12" />
                  <span>{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
              <b-popover
                target="stop-input"
                triggers="hover"
                placement="bottom"
                custom-class="basic-popover big">
                This can't be edited
              </b-popover>
            </b-form-group>

            <b-form-group>
              <label
                for="name"
                class="flex flex-align-center" >
                Display name
              </label>
              <ValidationProvider
                id="name-stop-input"
                v-slot="{ errors }"
                name="name"
                rules="required">
                <b-input
                  v-model="numberName"
                  autocomplete="off"
                  disabled
                  class="sb2-bordered sb2-hoverable sb2-focusable"
                  :class="{ approved: waitingOfficialAccount && waitingOfficialAccount.status === 'approved' }" />
                <SBIcon
                  v-if="waitingOfficialAccount && waitingOfficialAccount.status === 'approved'"
                  class="inline vertical-middle approved-icon"
                  :icon="SBIconList.GREEN_TICK"
                  color="#24d366"
                  :size="14" />
                <div
                  v-if="errors[0]"
                  class="error flex flex-align-center">
                  <SBIcon
                    class="mr-2"
                    :icon="SBIconList.ERROR"
                    :color="SBColors.redPrimary"
                    :size="12" />
                  <span>{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
              <b-popover
                target="name-stop-input"
                triggers="hover"
                placement="bottom"
                custom-class="basic-popover big">
                {{ waitingOfficialAccount && waitingOfficialAccount.status === 'approved' ? 'You have an official business account. Submit a request to edit your WhatsApp display name.' : 'Submit a request to edit your WhatsApp display name' }}
              </b-popover>
            </b-form-group>

            <!-- Display name START -->
            <!-- Initial view with submit button -->
            <b-form-group
              v-if="(
                !latestDisplayNameChange ||
                emptyObject(latestDisplayNameChange) ||
                latestDisplayNameChange.canceled ||
                ['approved', 'rejected'].includes(latestDisplayNameChange.status)
              )">
              <label class="flex flex-align-center" >
                Display name change
              </label>
              <div class="flex align-flex-end mt-minus-8">
                <p class="settings__label-sub greedy mb-0" >Request a WhatsApp display name change. <a href="https://www.facebook.com/business/help/378834799515077" target="_blank">Learn more</a>.</p>
                <span
                  class="request-btn"
                  @click="submitNameChange" >Submit request</span>
              </div>
            </b-form-group>
            <!-- Submitted view offering a cancel button -->
            <b-form-group
              v-else-if="!latestDisplayNameChange.canceled && latestDisplayNameChange.status === 'submitted'">
              <label class="flex flex-align-center" >
                <span class="emoji-fix">⏳</span> Your display name request has been submitted for review
              </label>
              <div class="flex align-flex-end mt-minus-8">
                <p class="settings__label-sub greedy mb-0" >You requested <span class="strong">{{ latestDisplayNameChange.name }}</span> to be your new WhatsApp display name.</p>
                <span
                  id="cancel-name-btn"
                  class="request-btn cancel"
                  @click="cancelNewDisplayName()" >
                  <span :class="{invisible: canceling}">Cancel request</span>
                  <Spinner
                    v-if="canceling"
                    style="height: 38px;"
                    class="my-4 full-width"
                    :size="16"
                    :thickness="2" />
                  <b-popover
                    target="cancel-name-btn"
                    triggers="hover"
                    placement="top"
                    custom-class="">
                    You can cancel your request before WhatsApp team starts the review.
                  </b-popover>
                </span>
              </div>
            </b-form-group>
            <!-- Pending view with no options -->
            <b-form-group
              v-else-if="!latestDisplayNameChange.canceled && latestDisplayNameChange.status === 'pending'">
              <label class="flex flex-align-center" >
                <span class="emoji-fix">⏳</span> Your display name request is under review
              </label>
              <div class="flex align-flex-end mt-minus-8">
                <p class="settings__label-sub greedy mb-0" >You requested <span class="strong">{{ latestDisplayNameChange.name }}</span> to be your new WhatsApp display name.</p>
              </div>
            </b-form-group>
            <!-- Rejected error message -->
            <div style="margin-top: -5px;">
              <div
                v-if="latestDisplayNameChange && !latestDisplayNameChange.seen && latestDisplayNameChange.status === 'rejected'"
                class="rejected inline flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.TRIANGLE_EXCLAMATION"
                  :color="SBColors.blackPrimary"
                  :size="10" />
                Your request to use display name <span class="strong">&nbsp;{{ latestDisplayNameChange.name }}&nbsp;</span> wasn't approved. Please submit another request.
                <SBIcon
                  class="ml-2"
                  :icon="SBIconList.CLOSE_THIN"
                  :color="SBColors.blackUltralight"
                  :hover-icon-color="SBColors.blackPrimary"
                  :size="8"
                  :hover-size="16"
                  :pointer="true"
                  @click="cancelDisplayNameReject" />
              </div>
            </div>
            
            <!-- Display name END -->

            <!-- Official Account START -->
            <!-- NOTE: If account is official then do not show this section. -->
            <!-- Rejected view -->
            <b-form-group
              v-if="(
                waitingOfficialAccount &&
                waitingOfficialAccount.status === 'rejected' &&
                daysFromLastOfficalRequest < 30
              )">
              <label class="flex flex-align-center" >
                Official business account
              </label>
              <div class="flex align-flex-end mt-minus-8">
                <p class="settings__label-sub greedy mb-0">
                  Request an official business number that has a green tick
                  <SBIcon
                    class="inline vertical-middle"
                    :icon="SBIconList.GREEN_TICK"
                    color="#24d366"
                    :size="14" />
                  next to its name. <a href="https:/developers.facebook.com/docs/whatsapp/overview/business-accounts/#official-business-account" target="_blank" >Learn more</a>.</p>
                <span
                  id="disabled-official-btn"
                  class="request-btn no-hover" >Submit request</span>
                <b-popover
                  target="disabled-official-btn"
                  triggers="hover"
                  placement="top"
                  custom-class="">
                  You must wait {{ daysToResubmitOfficialAccount - daysFromLastOfficalRequest }} {{ (daysToResubmitOfficialAccount - daysFromLastOfficalRequest) > 1 ? 'days' : 'day' }} to submit another request for this phone number.
                </b-popover>
              </div>
            </b-form-group>
            <!-- Initial state with submit button -->
            <b-form-group
              v-else-if="(
                !waitingOfficialAccount ||
                emptyObject(waitingOfficialAccount) ||
                waitingOfficialAccount.canceled ||
                waitingOfficialAccount.status === 'rejected'
              )">
              <label class="flex flex-align-center" >
                Official business account
              </label>
              <div class="flex align-flex-end mt-minus-8">
                <p class="settings__label-sub greedy mb-0">
                  Request an official business number that has a green tick
                  <SBIcon
                    class="inline vertical-middle"
                    :icon="SBIconList.GREEN_TICK"
                    color="#24d366"
                    :size="14" />
                  next to its name. <a href="https://developers.facebook.com/docs/whatsapp/overview/business-accounts/#official-business-account" target="_blank" >Learn more</a>.</p>
                <span
                  id="disabled-official-btn"
                  class="request-btn"
                  :class="{ 'no-hover': waitingOfficialAccount && !waitingOfficialAccount.canceled && waitingOfficialAccount.status === 'rejected' && daysFromLastOfficalRequest < 30 }"
                  @click="showOfficialAccountForm" >Submit request</span>
                <b-popover
                  v-if="waitingOfficialAccount && waitingOfficialAccount.status === 'rejected' && daysFromLastOfficalRequest < 30"
                  target="disabled-official-btn"
                  triggers="hover"
                  placement="top"
                  custom-class="">
                  You must wait {{ daysToResubmitOfficialAccount - daysFromLastOfficalRequest }} {{ (daysToResubmitOfficialAccount - daysFromLastOfficalRequest) > 1 ? 'days' : 'day' }} to submit another request for this phone number.
                </b-popover>
              </div>
            </b-form-group>
            <!-- Submitted view offering a cancel button -->
            <b-form-group
              v-else-if="(
                !waitingOfficialAccount.canceled &&
                waitingOfficialAccount.status === 'submitted'
              )">
              <label
                class="flex flex-align-center" >
                <span class="emoji-fix">⏳</span> Your official business account request has been submitted for review
              </label>
              <div class="flex align-flex-end mt-minus-8">
                <p class="settings__label-sub greedy mb-0">
                  You requested an official business number that has a green tick
                  <SBIcon
                    class="inline vertical-middle"
                    :icon="SBIconList.GREEN_TICK"
                    color="#24d366"
                    :size="14" />
                  next to its name.</p>
                <span
                  id="cancel-official-btn"
                  class="request-btn cancel"
                  @click="cancelOfficialAccount()" >
                  <span :class="{invisible: cancelingOfficial}">Cancel request</span>
                  <Spinner
                    v-if="cancelingOfficial"
                    style="height: 38px;"
                    class="my-4 full-width"
                    :size="16"
                    :thickness="2" />
                  <b-popover
                    target="cancel-official-btn"
                    triggers="hover"
                    placement="top"
                    custom-class="">
                    You can cancel your request before WhatsApp team starts the review.
                  </b-popover>
                </span>
              </div>
            </b-form-group>
            <!-- Pending view with no options -->
            <b-form-group
              v-else-if="(
                !waitingOfficialAccount.canceled &&
                waitingOfficialAccount.status === 'pending'
              )">
              <label
                class="flex flex-align-center" >
                <span class="emoji-fix">⏳</span> Your official business account request is under review
              </label>
              <div class="flex align-flex-end mt-minus-8">
                <p class="settings__label-sub greedy mb-0">
                  You requested an official business number that has a green tick
                  <SBIcon
                    class="inline vertical-middle"
                    :icon="SBIconList.GREEN_TICK"
                    color="#24d366"
                    :size="14" />
                  next to its name.</p>
              </div>
            </b-form-group>
            <!-- Rejected error message -->
            <div style="margin-top: -5px;">
              <div
                v-if="waitingOfficialAccount && !waitingOfficialAccount.seen && waitingOfficialAccount.status === 'rejected'"
                class="rejected inline flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.TRIANGLE_EXCLAMATION"
                  :color="SBColors.blackPrimary"
                  :size="10" />
                Your official business account request wasn't approved. You can submit another request {{ timeForNewRequest }}.
                <SBIcon
                  class="ml-2"
                  :icon="SBIconList.CLOSE_THIN"
                  :color="SBColors.blackUltralight"
                  :hover-icon-color="SBColors.blackPrimary"
                  :size="8"
                  :hover-size="16"
                  :pointer="true"
                  @click="cancelOfficialAccountReject" />
              </div>
            </div>
            <!-- Official Account END -->

            <b-form-group style="padding-top: 10px;">
              <label
                for="about"
                class="flex flex-align-center" >
                <span class="greedy">About</span>
                <span class="character-counter">{{ aboutMax - about.length }}</span>
              </label>
              <ValidationProvider
                v-slot="{ errors }"
                name="about"
                rules="">
                <b-input
                  id="about"
                  v-model="about"
                  autocomplete="off"
                  :maxlength="aboutMax"
                  class="sb2-bordered sb2-hoverable sb2-focusable"
                  placeholder="Tell customers briefly about your company" />
                <div
                  v-if="errors[0]"
                  class="error flex flex-align-center">
                  <SBIcon
                    class="mr-2"
                    :icon="SBIconList.ERROR"
                    :color="SBColors.redPrimary"
                    :size="12" />
                  <span>{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </b-form-group>

            <b-form-group>
              <label>
                Category
              </label>
              <ValidationProvider
                v-slot="{ errors }"
                rules=""
                name="category" >
                <multiselect
                  v-model="category"
                  placeholder="Select a category"
                  class="flex-options hide-tags no-selected-background"
                  :options="categoryOptions"
                  label="text"
                  track-by="value"
                  :multiple="false"
                  :show-labels="false"
                  :show-no-results="false"
                  :close-on-select="true"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :allow-empty="false"
                  :searchable="false" >
                  <template
                    slot="option"
                    slot-scope="props">
                    <SBIcon
                      :icon="SBIconList.DONE_TICK"
                      :color="SBColors.bluePrimary"
                      :size="9"/>
                    {{ props.option.text }}
                  </template>
                </multiselect>
                <div
                  v-if="errors[0]"
                  class="error flex flex-align-center">
                  <SBIcon
                    class="mr-2"
                    :icon="SBIconList.ERROR"
                    :color="SBColors.redPrimary"
                    :size="12" />
                  <span>Required field</span>
                </div>
              </ValidationProvider>
            </b-form-group>

            <b-form-group>
              <label
                for="address"
                class="flex flex-align-center" >
                <span class="greedy">Address</span>
                <span class="character-counter">{{ addressMax - address.length }}</span>
              </label>
              <ValidationProvider
                v-slot="{ errors }"
                name="Address"
                rules="">
                <b-input
                  id="address"
                  v-model="address"
                  autocomplete="off"
                  :maxlength="addressMax"
                  class="sb2-bordered sb2-hoverable sb2-focusable"
                  placeholder="Enter business address" />
                <div
                  v-if="errors[0]"
                  class="error flex flex-align-center">
                  <SBIcon
                    class="mr-2"
                    :icon="SBIconList.ERROR"
                    :color="SBColors.redPrimary"
                    :size="12" />
                  <span>{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </b-form-group>

            <b-form-group>
              <label
                for="description"
                class="flex flex-align-center" >
                <span class="greedy">Description</span>
                <span class="character-counter">{{ descriptionMax - description.length }}</span>
              </label>
              <ValidationProvider
                v-slot="{ errors }"
                name="description"
                rules="">
                <b-form-textarea
                  id="description"
                  v-model="description"
                  autocomplete="off"
                  :maxlength="descriptionMax"
                  class="sb2-bordered sb2-hoverable sb2-focusable"
                  placeholder="Tell customers more about your company"
                  rows="3"
                  max-rows="6" />
                <div
                  v-if="errors[0]"
                  class="error flex flex-align-center">
                  <SBIcon
                    class="mr-2"
                    :icon="SBIconList.ERROR"
                    :color="SBColors.redPrimary"
                    :size="12" />
                  <span>{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </b-form-group>

            <b-form-group>
              <label
                for="email"
                class="flex flex-align-center" >
                <span class="greedy">Email</span>
                <span class="character-counter">{{ emailMax - email.length }}</span>
              </label>
              <ValidationProvider
                v-slot="{ errors }"
                name="email"
                rules="email"
                mode="eager" >
                <b-input
                  id="email"
                  v-model="email"
                  autocomplete="off"
                  :maxlength="emailMax"
                  class="sb2-bordered sb2-hoverable sb2-focusable"
                  placeholder="email@business.com" />
                <div
                  v-if="errors[0]"
                  class="error flex flex-align-center">
                  <SBIcon
                    class="mr-2"
                    :icon="SBIconList.ERROR"
                    :color="SBColors.redPrimary"
                    :size="12" />
                  <span>{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </b-form-group>

            <b-form-group>
              <label
                for="website1"
                class="flex flex-align-center" >
                <span class="greedy">Website 1</span>
                <span class="character-counter">{{ websiteMax - website1.length }}</span>
              </label>
              <ValidationProvider
                v-slot="{ errors }"
                name="website 1"
                rules="urlPrefixed">
                <b-input
                  id="website1"
                  v-model="website1"
                  autocomplete="off"
                  :maxlength="websiteMax"
                  class="sb2-bordered sb2-hoverable sb2-focusable"
                  placeholder="www.website1.com" />
                <div
                  v-if="errors[0]"
                  class="error flex flex-align-center">
                  <SBIcon
                    class="mr-2"
                    :icon="SBIconList.ERROR"
                    :color="SBColors.redPrimary"
                    :size="12" />
                  <span>{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </b-form-group>
            <b-form-group>
              <label
                for="website1"
                class="flex flex-align-center" >
                <span class="greedy">Website 2</span>
                <span class="character-counter">{{ websiteMax - website2.length }}</span>
              </label>
              <ValidationProvider
                v-slot="{ errors }"
                name="website 2"
                rules="urlPrefixed">
                <b-input
                  id="website2"
                  v-model="website2"
                  autocomplete="off"
                  :maxlength="websiteMax"
                  class="sb2-bordered sb2-hoverable sb2-focusable"
                  placeholder="www.website2.com" />
                <div
                  v-if="errors[0]"
                  class="error flex flex-align-center">
                  <SBIcon
                    class="mr-2"
                    :icon="SBIconList.ERROR"
                    :color="SBColors.redPrimary"
                    :size="12" />
                  <span>{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </b-form-group>
          </form>

          <span
            v-if="errorMessage"
            class="error-message flex">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.ERROR"
              :color="SBColors.redPrimary"
              :size="12" />
            {{ errorMessage }}
          </span>

          <FooterActions
            :form-valid="valid"
            :has-changes="dirty || !!(processedMedia && processedMedia.url)"
            :show-cancel="dirty || !!(processedMedia && processedMedia.url)"
            :save-busy="saving"
            @reset="resetChanges"
            @save="updateInfo" />
        </ValidationObserver>
      </template>
    </div>

    <WhatsAppDisplayNameChangeModal
      ref="whatsAppDisplayNameChangeModalRef"
      :business="business"
      :current-display-name="numberName"
      @display-name-change="newDisplayNameRequest" />

    <WhatsAppOfficialAccountModal
      ref="whatsAppOfficialAccountModalRef"
      :business="business"
      @official-requested="officialAccountRequested" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';

import DropHere from '@/components/common/DropHere';
import { getFileExtension, getMediaType } from '@/helpers/mediaHelpers';
import ToasterMixin from '@/mixins/Toaster';
import NoPermissionMixin from '@/mixins/AccessPermission';
import FooterActions from '../partials/FooterActions';
import SBColors from '@/helpers/lists/colors';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';
import api from '@/helpers/api';
import Spinner from '@/components/common/Spinner';
import WhatsAppDisplayNameChangeModal from '@/components/business/settings/modals/WhatsAppDisplayNameChange.modal';
import WhatsAppOfficialAccountModal from '@/components/business/settings/modals/WhatsAppOfficialAccount.modal';
import CommonActions from '@/components/manage/CommonActions.mixin';

export default {
  name: 'WhatsAppProfileSettings',
  components: {
    FooterActions,
    DropHere,
    Multiselect,
    Spinner,
    WhatsAppDisplayNameChangeModal,
    WhatsAppOfficialAccountModal,
  },
  mixins: [
    ToasterMixin,
    NoPermissionMixin,
    UserPermissionsMixin,
    CommonActions,
  ],
  data() {
    return {
      daysToResubmitOfficialAccount: 30,
      errorMessage: '',
      waProfileLogo: '',
      saving: false,
      processedMedia: {},
      SBColors,
      initialized: false,
      waitingOfficialAccount: {},
      canceling: false,
      cancelingOfficial: false,
      latestDisplayNameChange: {},
      loading: false,
      categoryOptions: [],
      numberName: '',
      about: '',
      aboutMax: 139,
      category: null,
      address: '',
      addressMax: 256,
      description: '',
      descriptionMax: 512,
      email: '',
      emailMax: 128,
      websiteMax: 256,
      website1: '',
      website2: '',
      resetTo: {
        numberName: '',
        about: '',
        category: null,
        address: '',
        description: '',
        email: '',
        website1: '',
        website2: '',
      },
    };
  },
  computed: {
    ...mapGetters({
      apiError: 'organisation/apiError',
      currentOrganisation: 'organisation/currentOrganisation',
    }),
    business() {
      return this.currentOrganisation.businesses.find(b => b.id === this.$route.params.businessId);
    },
    logoUrl() {
      return !this.resettingData && this.processedMedia.url ? this.processedMedia.url : this.waProfileLogo;
    },
    daysFromLastOfficalRequest() {
      if (!this.waitingOfficialAccount || !this.waitingOfficialAccount.updated_at) {
        return 31;
      }
      const daysDiff = this.$moment().diff(this.$moment(false, false).unix(this.waitingOfficialAccount.updated_at), 'days');
      return daysDiff;
    },
    timeForNewRequest() {
      return (this.daysFromLastOfficalRequest < 30) ? `in ${this.daysToResubmitOfficialAccount - this.daysFromLastOfficalRequest} ${(this.daysToResubmitOfficialAccount - this.daysFromLastOfficalRequest) > 1 ? 'days' : 'day'}` : 'now';
    },
  },
  watch: {
    apiError(error) {
      this.errorMessage = error.data.message;
    },
    business() {
      this.processedMedia = {};
      this.getWAProfile();
    },
  },
  beforeMount() {
    if (this.checkPermission('manage_settings')) {
      api('GET', '/whatsapp/settings', null, null, 'w')
        .then((response) => {
          if (response.status === 200) {
            Object.keys(response.data.profile.category).forEach((categoryKey) => {
              this.categoryOptions.push({
                value: categoryKey,
                text: response.data.profile.category[categoryKey],
              });

              // In case wa settings response comes after the profile response
              if (this.category && this.category.setMe && this.category.setMe === categoryKey) {
                this.category = {
                  value: categoryKey,
                  text: response.data.profile.category[categoryKey],
                };
                this.resetTo.category = {
                  value: categoryKey,
                  text: response.data.profile.category[categoryKey],
                };
              }
            });
          }
        });
    }
  },
  mounted() {
    this.finalizeSplashView();

    this.$eventBus.$on('mediaDrop', this.mediaDropHandler);
    this.$eventBus.$on('dropStats', this.dropStats);

    if (this.checkPermission('manage_settings') && this.business && this.business.id) {
      this.getWAProfile();
    }
  },
  beforeDestroy() {
    this.$eventBus.$off('mediaDrop', this.mediaDropHandler);
    this.$eventBus.$off('dropStats', this.dropStats);
  },
  methods: {
    ...mapActions({
      finalizeSplashView: 'organisation/finalizeSplashView',
      updateOrganisationInfo: 'organisation/updateOrganisationInfo',
    }),
    cancelDisplayNameReject() {
      this.latestDisplayNameChange.seen = true;
      this.latestDisplayNameChange = { ...this.latestDisplayNameChange };
      api('PUT', `organisation/${this.currentOrganisation.id}/business/${this.business.id}/whatsapp/display-name-change-request/${this.latestDisplayNameChange.id}`, { seen: true, includeMe: true }, null, 'w');
    },
    cancelOfficialAccountReject() {
      this.waitingOfficialAccount.seen = true;
      this.waitingOfficialAccount = { ...this.waitingOfficialAccount };
      api('PUT', `organisation/${this.currentOrganisation.id}/business/${this.business.id}/whatsapp/green-badge-request/${this.waitingOfficialAccount.id}`, { seen: true }, null, 'w');
    },
    cancelNewDisplayName() {
      const params = {
        title: 'Cancel request',
        message: 'Are you sure you want to cancel a request for WhatsApp display name change?',
        confirmButtonText: 'Confirm',
        buttonVariant: 'RED',
        confirmCallback: () => {
          this.canceling = true;

          // This prevents loader for cancel action from showing up. Just comment these two lines in order to get that functionality back
          this.latestDisplayNameChange.canceled = true;
          this.latestDisplayNameChange = { ...this.latestDisplayNameChange };
          //

          const data = {
            cancel: true,
          };
          api('PUT', `organisation/${this.currentOrganisation.id}/business/${this.business.id}/whatsapp/display-name-change-request/${this.latestDisplayNameChange.id}`, data, null, 'w')
            .then((request) => {
              if (request.status === 200) {
                this.latestDisplayNameChange.canceled = true;
                this.latestDisplayNameChange = { ...this.latestDisplayNameChange };
                this.showToaster('<span class="emoji-fix">👍</span> WhatsApp display name change was successfully canceled');
              } else {
                this.latestDisplayNameChange.canceled = false;
                this.latestDisplayNameChange = { ...this.latestDisplayNameChange };
              }
            })
            .catch(() => {
              this.latestDisplayNameChange.canceled = false;
              this.latestDisplayNameChange = { ...this.latestDisplayNameChange };
              this.latestDisplayNameChange.status = 'pending';
              this.showToaster('<span class="emoji-fix">⏳</span> Your display name request is already under review');
            })
            .finally(() => {
              this.canceling = false;
            });
        },
      };
      this.$confirmPluginModal.show(params);
    },
    cancelOfficialAccount() {
      const params = {
        title: 'Cancel request',
        message: 'Are you sure you want to cancel a request for an official business account?',
        confirmButtonText: 'Confirm',
        buttonVariant: 'RED',
        confirmCallback: () => {
          this.cancelingOfficial = true;

          // This prevents loader for cancel action from showing up. Just comment these two lines in order to get that functionality back
          this.waitingOfficialAccount.canceled = true;
          this.waitingOfficialAccount = { ...this.waitingOfficialAccount };
          //

          const data = {
            cancel: true,
          };
          api('PUT', `organisation/${this.currentOrganisation.id}/business/${this.business.id}/whatsapp/green-badge-request/${this.waitingOfficialAccount.id}`, data, null, 'w')
            .then((request) => {
              if (request.status === 200) {
                this.waitingOfficialAccount.canceled = true;
                this.waitingOfficialAccount = { ...this.waitingOfficialAccount };
                this.showToaster('<span class="emoji-fix">👍</span> Official business account request was successfully canceled');
              } else {
                this.waitingOfficialAccount.canceled = false;
                this.waitingOfficialAccount = { ...this.waitingOfficialAccount };
              }
            })
            .catch(() => {
              this.waitingOfficialAccount.canceled = false;
              this.waitingOfficialAccount = { ...this.waitingOfficialAccount };
              this.waitingOfficialAccount.status = 'pending';
              this.showToaster('<span class="emoji-fix">⏳</span> Your official business account request is already under review');
            })
            .finally(() => {
              this.cancelingOfficial = false;
            });
        },
      };
      this.$confirmPluginModal.show(params);
    },
    getWAProfile() {
      this.loading = true;
      api('GET', `organisation/${this.currentOrganisation.id}/business/${this.business.id}/whatsapp/profile`, null, null, 'w')
        .then((response) => {
          if (response.status === 200) {
            this.numberName = response.data.name || '';
            this.about = response.data.about || '';
            this.category = this.categoryOptions.length ? this.categoryOptions.find(co => co.value === response.data.category) : { setMe: response.data.category };
            this.address = response.data.address || '';
            this.description = response.data.description || '';
            this.email = response.data.email || '';
            this.website1 = response.data.website1 || '';
            this.website2 = response.data.website2 || '';
            this.waProfileLogo = response.data.logo || '';
            this.latestDisplayNameChange = { ...response.data.latest_display_name_change } || {};
            this.waitingOfficialAccount = response.data.latest_green_badge_request;

            this.resetTo = {
              numberName: response.data.name || '',
              about: response.data.about || '',
              category: this.categoryOptions.length ? this.categoryOptions.find(co => co.value === response.data.category) : { setMe: response.data.category },
              address: response.data.address || '',
              description: response.data.description || '',
              email: response.data.email || '',
              website1: response.data.website1 || '',
              website2: response.data.website2 || '',
              waProfileLogo: response.data.logo || '',
            };
          }
        })
        .finally(() => {
          this.loading = false;
          this.initialized = true;
        });
    },
    newDisplayNameRequest(data) {
      this.latestDisplayNameChange = { ...data };
    },
    officialAccountRequested(data) {
      this.waitingOfficialAccount = { ...data };
    },
    resetChanges() {
      this.numberName = this.resetTo.numberName;
      this.about = this.resetTo.about;
      this.category = this.resetTo.category;
      this.address = this.resetTo.address;
      this.description = this.resetTo.description;
      this.email = this.resetTo.email;
      this.website1 = this.resetTo.website1;
      this.website2 = this.resetTo.website2;
      this.processedMedia = {};
      this.waProfileLogo = this.resetTo.waProfileLogo;
      this.$refs.customFieldObserver.reset();
    },
    showOfficialAccountForm() {
      if (
        this.waitingOfficialAccount &&
        !this.waitingOfficialAccount.canceled &&
        this.waitingOfficialAccount.status === 'rejected' &&
        this.daysFromLastOfficalRequest < 30
      ) {
        return;
      }
      // No need to show this modal any more since the switch to coud API
      // this.$refs.whatsAppOfficialAccountModalRef.show();

      if (this.business.whatsapp.business_id && this.business.whatsapp.waba_id) {
        window.open(`https://business.facebook.com/wa/manage/phone-numbers/?business_id=${this.business.whatsapp.business_id}&waba_id=${this.business.whatsapp.waba_id}&phone_number=${this.business.phone}&childRoute=PHONE_PROFILE%2FPROFILE`, '_blank');
      } else {
        window.open('https://developers.facebook.com/docs/whatsapp/overview/business-accounts/#official-business-account', '_blank');
      }
    },
    submitNameChange() {
      // No need to show this modal any more since the switch to coud API
      // this.$refs.whatsAppDisplayNameChangeModalRef.show();
      if (this.business.whatsapp.business_id && this.business.whatsapp.waba_id) {
        window.open(`https://business.facebook.com/wa/manage/phone-numbers/?business_id=${this.business.whatsapp.business_id}&waba_id=${this.business.whatsapp.waba_id}`, '_blank');
      } else {
        window.open('https://www.facebook.com/business/help/378834799515077', '_blank');
      }
    },
    updateInfo() {
      this.$refs.customFieldObserver.validate()
        .then((valid) => {
          if (!valid) {
            return;
          }

          const formData = new FormData();
          this.saving = true;

          if (this.email) {
            formData.append('email', this.email);
          }
          if (this.website1) {
            formData.append('website1', this.website1);
          }
          if (this.website2) {
            formData.append('website2', this.website2);
          }
          if (this.address) {
            formData.append('address', this.address);
          }
          if (this.about) {
            formData.append('about', this.about);
          }
          if (this.description) {
            formData.append('description', this.description);
          }
          if (this.category) {
            formData.append('vertical', this.category.value);
          }
          if (this.processedMedia && this.processedMedia.file) {
            formData.append('profile_picture', this.processedMedia.file);
          }

          api('PUT', `organisation/${this.currentOrganisation.id}/business/${this.business.id}/whatsapp/profile`, formData, null, 'w')
            .then((response) => {
              if (response.status === 200) {
                this.showToaster('Profile info has been successfully updated.', null, null, 'OK');
                this.resetTo = {
                  numberName: this.numberName,
                  about: this.about,
                  category: this.category,
                  address: this.address,
                  description: this.description,
                  email: this.email,
                  website1: this.website1,
                  website2: this.website2,
                  waProfileLogo: this.processedMedia.file,
                };
              }
            })
            .catch(() => {
              this.showToaster('Something went wrong. Please try again.', null, null, 'error');
            })
            .finally(() => {
              this.resetChanges();
              this.$nextTick(() => {
                this.saving = false;
              });
            });
        });
    },
    dropStats(stats) {
      if (
        (stats.unsupportedMedia.length > 0) &&
        (stats.supportedMedia.length === 0) &&
        !this.visible
      ) {
        this.showToaster('The file format is not supported.', null, null, 'OK');
      }
    },
    /**
     * Handler for media drop event. Accepts single file or array of files.
     *
     * @param droppedMedia Media file(s) dropped on the messages section.
     */
    mediaDropHandler(droppedMedia) {
      if (Array.isArray(droppedMedia)) {
        this.processMedia(droppedMedia[0]);
      } else {
        this.processMedia(droppedMedia);
      }
    },
    /**
     * Processes media file setting it url, thumbnail for video, caption, type etc.
     *
     * @param media Media to process.
     */
    processMedia(media) {
      let newMedia = {};
      const URLObj = window.URL || window.webkitURL;
      const mediaType = media.file.type ? getMediaType(media.file.type) : getMediaType(getFileExtension(media.name));

      if (mediaType === 'video') {
        return;
      }

      newMedia = {
        id: media.id,
        name: media.name,
        url: URLObj.createObjectURL(media.file),
        thumbnail: null,
        caption: '',
        mediaType,
        file: media.file,
      };

      this.processedMedia = newMedia;
      this.processedMedia = { ...this.processedMedia };
    },
  },
};
</script>

<style lang="scss" scoped>
.approved {
  background-color: #f2fff7 !important;
  padding-left: 28px;
}

.rejected {
  margin: 0px 0 20px;
  padding: 4px 10px 5px;
  border-radius: 10px;
  border: solid 1px #f5edce;
  background-color: #fff7dd;
  font-size: 12px;
}

#name-stop-input {
  position: relative;

  .approved-icon {
    position: absolute;
    top: 9px;
    left: 10px;
  }
}

.profile-btn-wrapper {
  padding-right: 12px !important;
  &:hover ::v-deep .sb-icon .icon.mask {
    background-color: $blackPrimary !important;
  }
}

.form-group label * {
  padding: 0 !important;
  margin: 0 !important;
  color: $blackPrimary !important;
  font-weight: 500 !important;
  text-transform: capitalize !important;
}

.form-group label .character-counter {
  font-size: 14px !important;
  font-weight: normal !important;
  color: $greyPrimary !important;
}

.form-group .strong {
  font-weight: 500 !important;
}

.website-2 {
  margin-top: 10px;
  margin-bottom: 6px;
}

.settings__label-sub a {
  color: #2680eb !important;
}

.settings__label-sub.mb-0 {
  margin-bottom: 0 !important;
}

.request-btn {
  padding: 7px 16px;
  border-radius: 5px;
  background-color: #edf0f2;
  font-size: 12px !important;
  font-weight: 500 !important;
  position: relative;

  * {
    font-size: 12px !important;
    font-weight: 500 !important;
  }

  &.no-hover {
    color: $blackUltralight;
    cursor: default !important;
  }

  &.cancel {
    background-color: #fff5f6;
  }

  &:not(.no-hover):hover {
    cursor: pointer;
    background-color: #e4e7ea;

    &.cancel {
      background-color: #faf0f1;
    }
  }

  .loader {
    position: absolute;
    top: 8px;
    margin: 0 0 0 0 !important;
    padding: 0 0 0 0;
  }
}

.mt-minus-8 {
  margin-top: -8px;
  min-height: 28px;
}

textarea {
  overflow-y: auto !important;
  padding: 8px 10px !important;
}


.wrapper {
  position: relative;
}

.settings--limit-width {
  height: unset !important;
}

#logo-wrapper {
  width: 76px;
  height: 76px;
  margin: 31px 0px 11px;
  padding: 4px;
  border-radius: 5px;
  position: relative;

  &:hover {
    background-color: $linkHover;
    cursor: pointer;
  }

  #logo {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 500px;
    background-color: #deedfe;
    background-image: url('~@/assets/img/SB-Icons/user-business-blue.svg');
    background-size: 35px;
    background-repeat: no-repeat;
    background-position: center;
  }
}
</style>
